import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { statusColor, pretty, BOOKING_STATUSES } from "@/lib/helpers";
import { PageHeader } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, CalendarClock, AlertTriangle, Grid3X3 } from "lucide-react";
import { toast } from "sonner";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function NewBookingDialog({ rid, onCreated, walkIn, defaultDate }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ customer_name: "", customer_phone: "", booking_date: defaultDate, booking_time: "19:00", guest_count: 2, allergy_dietary_note: "", notes: "" });
  const up = (k, v) => setF((p) => ({ ...p, [k]: v }));
  useEffect(() => { setF((p) => ({ ...p, booking_date: defaultDate })); }, [defaultDate]);

  const submit = async () => {
    if (!f.customer_name) return toast.error("Enter a name");
    try {
      await api.post(`/restaurants/${rid}/bookings`, {
        ...f, guest_count: +f.guest_count,
        source: walkIn ? "WALK_IN" : "STAFF_DASHBOARD",
        allergy_dietary_note: f.allergy_dietary_note || null,
      });
      toast.success(walkIn ? "Walk-in seated" : "Booking created");
      setOpen(false); onCreated();
    } catch { toast.error("Could not create booking"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="h-11 gap-2" variant={walkIn ? "outline" : "default"}
          data-testid={walkIn ? "walkin-booking-button" : "new-booking-button"}>
          <Plus className="h-4 w-4" /> {walkIn ? "Seat walk-in" : "New booking"}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-display">{walkIn ? "Seat walk-in now" : "Create booking"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><Label>Customer name</Label>
            <Input className="h-11 mt-1.5" value={f.customer_name} onChange={(e) => up("customer_name", e.target.value)} data-testid="booking-name" /></div>
          <div><Label>Phone</Label><Input className="h-11 mt-1.5" value={f.customer_phone} onChange={(e) => up("customer_phone", e.target.value)} /></div>
          <div><Label>Guests</Label><Input type="number" min={1} className="h-11 mt-1.5" value={f.guest_count} onChange={(e) => up("guest_count", e.target.value)} data-testid="booking-guests" /></div>
          <div><Label>Date</Label><Input type="date" className="h-11 mt-1.5" value={f.booking_date} onChange={(e) => up("booking_date", e.target.value)} /></div>
          <div><Label>Time</Label><Input className="h-11 mt-1.5" placeholder="19:00" value={f.booking_time} onChange={(e) => up("booking_time", e.target.value)} data-testid="booking-time" /></div>
          <div className="sm:col-span-2"><Label>Allergy / dietary note</Label>
            <Textarea className="mt-1.5" value={f.allergy_dietary_note} onChange={(e) => up("allergy_dietary_note", e.target.value)}
              placeholder="Any allergies or dietary requirements? (flags for staff review)" data-testid="booking-allergy-note" /></div>
        </div>
        <DialogFooter><Button onClick={submit} className="h-11" data-testid="submit-booking">Save booking</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssignTablesDialog({ booking, tables, onSaved }) {
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(booking.table_ids || []);
  useEffect(() => { if (open) setSel(booking.table_ids || []); }, [open, booking.table_ids]);
  const toggle = (id) => setSel((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const save = async () => {
    await api.put(`/bookings/${booking.id}`, { table_ids: sel });
    toast.success("Tables assigned"); setOpen(false); onSaved();
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="h-9 gap-1.5" data-testid={`assign-tables-${booking.id}`}>
          <Grid3X3 className="h-3.5 w-3.5" /> {booking.table_ids?.length ? `${booking.table_ids.length} table(s)` : "Assign tables"}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-display">Assign tables · {booking.customer_name}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">The selected tables will show as reserved around {booking.booking_time} on {booking.booking_date}.</p>
        <div className="flex flex-wrap gap-2 py-2">
          {tables.map((t) => (
            <button key={t.id} data-testid={`assign-table-${t.id}`} onClick={() => toggle(t.id)}
              className={`h-10 px-3 rounded-lg text-sm border ${sel.includes(t.id) ? "bg-primary text-primary-foreground border-primary" : "bg-secondary border-border"}`}>
              {t.table_name} · {t.capacity}
            </button>
          ))}
          {tables.length === 0 && <p className="text-sm text-muted-foreground">No tables yet — add them in Settings.</p>}
        </div>
        <DialogFooter><Button onClick={save} className="h-11" data-testid={`save-assign-${booking.id}`}>Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Bookings() {
  const { rid } = useParams();
  const [bookings, setBookings] = useState([]);
  const [tables, setTables] = useState([]);
  const [selected, setSelected] = useState(new Date());
  const todayRef = useRef(null);

  const load = useCallback(() => {
    api.get(`/restaurants/${rid}/bookings`).then((r) => setBookings(r.data)).catch(() => {});
    api.get(`/restaurants/${rid}/tables`).then((r) => setTables(r.data)).catch(() => {});
  }, [rid]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setTimeout(() => todayRef.current?.scrollIntoView({ inline: "center", block: "nearest" }), 200);
    return () => clearTimeout(t);
  }, []);

  const setStatus = async (b, status) => {
    await api.patch(`/bookings/${b.id}/status`, { status });
    toast.success("Booking updated"); load();
  };

  const selDate = iso(selected);
  const dayBookings = useMemo(
    () => bookings.filter((b) => b.booking_date === selDate).sort((a, b) => (a.booking_time || "").localeCompare(b.booking_time || "")),
    [bookings, selDate]);
  const weekDates = useMemo(() => {
    const year = new Date().getFullYear();
    const start = new Date(year, 0, 1);
    const end = new Date(year, 11, 31);
    const out = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) out.push(new Date(d));
    return out;
  }, []);
  const hourRange = useMemo(() => {
    let min = 9, max = 22;
    dayBookings.forEach((b) => { const h = parseInt(b.booking_time, 10); if (!isNaN(h)) { min = Math.min(min, h); max = Math.max(max, h); } });
    const arr = []; for (let h = min; h <= max; h++) arr.push(h); return arr;
  }, [dayBookings]);
  const byHour = useMemo(() => {
    const g = {}; dayBookings.forEach((b) => { const h = parseInt(b.booking_time, 10); (g[h] = g[h] || []).push(b); }); return g;
  }, [dayBookings]);
  const tableName = (id) => tables.find((t) => t.id === id)?.table_name || "?";
  const pad = (n) => String(n).padStart(2, "0");
  const todayStr = iso(new Date());

  return (
    <div>
      <PageHeader title="Bookings" subtitle="Calendar view — AI phone, staff and walk-in bookings, with table assignment.">
        <NewBookingDialog rid={rid} onCreated={load} walkIn defaultDate={selDate} />
        <NewBookingDialog rid={rid} onCreated={load} defaultDate={selDate} />
      </PageHeader>

      {/* Full-year scrollable date filter (auto-scrolls to today) */}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-6" data-testid="bookings-week-strip">
        {weekDates.map((d) => {
          const dStr = iso(d);
          const count = bookings.filter((b) => b.booking_date === dStr).length;
          const active = dStr === selDate;
          return (
            <button key={dStr} data-testid={`week-date-${dStr}`} onClick={() => setSelected(d)}
              ref={dStr === todayStr ? todayRef : null}
              className={`shrink-0 w-[4.5rem] rounded-xl border p-2.5 text-center transition-colors ${active ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary/40"} ${dStr === todayStr && !active ? "ring-2 ring-primary/40" : ""}`}>
              <p className="text-[11px] uppercase tracking-wide opacity-80">{d.toLocaleDateString(undefined, { weekday: "short" })}</p>
              <p className="text-2xl font-display font-bold leading-tight">{d.getDate()}</p>
              <p className="text-[10px] opacity-70">{d.toLocaleDateString(undefined, { month: "short" })}</p>
              <div className="h-4 mt-0.5 flex items-center justify-center">
                {count > 0
                  ? <span className={`text-[10px] rounded-full px-1.5 font-medium ${active ? "bg-primary-foreground/20" : "bg-primary/10 text-primary"}`}>{count}</span>
                  : dStr === todayStr && !active ? <span className="text-[9px] text-primary">Today</span> : null}
              </div>
            </button>
          );
        })}
      </div>

      <h3 className="font-display font-bold text-lg mb-3">
        {selected.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}
        <span className="text-muted-foreground font-normal text-sm ml-2">{dayBookings.length} booking(s)</span>
      </h3>

      {/* Hourly time-slot day view */}
      <div className="rounded-2xl border border-border overflow-hidden" data-testid="bookings-day-list">
        {hourRange.map((h) => {
          const slot = (byHour[h] || []).sort((a, b) => (a.booking_time || "").localeCompare(b.booking_time || ""));
          return (
            <div key={h} className="flex gap-4 border-t border-border first:border-t-0 px-3 py-2 min-h-14" data-testid={`hour-slot-${h}`}>
              <div className="w-14 shrink-0 text-sm text-muted-foreground pt-1.5 font-mono">{pad(h)}:00</div>
              <div className="flex-1 grid sm:grid-cols-2 gap-2.5">
                {slot.length === 0 && <div className="text-xs text-muted-foreground/40 py-2">—</div>}
                {slot.map((b) => (
                  <Card key={b.id} data-testid={`booking-card-${b.id}`} className="p-3 rounded-xl border-l-4 border-l-primary/60">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-medium truncate">{b.booking_time} · {b.customer_name}</p>
                        <p className="text-xs text-muted-foreground">{b.guest_count} guests · {pretty(b.source)}</p>
                      </div>
                      <Badge className={statusColor(b.status)} variant="outline">{pretty(b.status)}</Badge>
                    </div>
                    {b.table_ids?.length > 0 && <p className="text-xs mt-1.5">Tables: {b.table_ids.map(tableName).join(", ")}</p>}
                    {b.allergy_dietary_note && (
                      <div className="mt-2 rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700 flex gap-1.5">
                        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {b.allergy_dietary_note}
                      </div>
                    )}
                    <div className="mt-2 flex items-center gap-2">
                      <Select value={b.status} onValueChange={(v) => setStatus(b, v)}>
                        <SelectTrigger className="h-9 flex-1 text-sm" data-testid={`booking-status-${b.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{BOOKING_STATUSES.map((s) => <SelectItem key={s} value={s}>{pretty(s)}</SelectItem>)}</SelectContent>
                      </Select>
                      <AssignTablesDialog booking={b} tables={tables} onSaved={load} />
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {dayBookings.length === 0 && (
        <div className="text-center py-10 text-muted-foreground">
          <CalendarClock className="h-10 w-10 mx-auto mb-3 opacity-40" /> No bookings on this day.
        </div>
      )}
    </div>
  );
}
